/**
 * Summary videos embedded on insight articles, keyed by post slug.
 * A slug with no entry renders no video and no VideoObject node, so articles
 * stay untouched until a video is listed here.
 *
 * `durationIso` and `uploadDate` feed both the visible label and the VideoObject
 * structured data, so they must describe the video actually embedded.
 */
export type PostVideo = {
  youtubeId: string;
  title: string;
  durationIso: string;
  uploadDate: string;
};

export const postVideos: Record<string, PostVideo> = {
  "revenue-leakage": {
    youtubeId: "KJ2dw1rRkLQ",
    title: "Revenue leakage: where the money goes missing between the quote and the bank",
    durationIso: "PT1M12S",
    uploadDate: "2026-10-28",
  },
};

/** The video listed for a slug, or undefined when the article has none. */
export const videoFor = (slug: string): PostVideo | undefined => postVideos[slug];

/** Portrait (9:16) frame for the facade, with the landscape card as fallback. */
export const videoThumbUrl = (youtubeId: string) => `https://i.ytimg.com/vi/${youtubeId}/oar2.jpg`;
/** Landscape share card: the schema thumbnail and the facade's fallback frame. */
export const videoCardUrl = (youtubeId: string) => `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;

/** Cookie-less embed origin. Query parameters are added by the player itself. */
export const videoEmbedUrl = (youtubeId: string) => `https://www.youtube-nocookie.com/embed/${youtubeId}`;

/**
 * Plain-language length for the label, read from `durationIso` so it stays true
 * when a longer summary is added: "about 70 seconds". Empty when unreadable.
 */
export const durationLabel = (iso: string): string => {
  const match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(iso);
  if (!match) return "";
  const total = Number(match[1] ?? 0) * 3600 + Number(match[2] ?? 0) * 60 + Number(match[3] ?? 0);
  if (!total) return "";
  if (total < 120) return `about ${Math.round(total / 10) * 10} seconds`;
  const minutes = Math.max(1, Math.round(total / 60));
  return `about ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
};
